import React, { useState, useEffect, useCallback } from "react";
import { Form, InputNumber, Button, message, Card, Row, Col, Spin } from "antd";
import axios from "axios";
import PageHeader from "../Layout/PageHeader";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const RedemptionSettings = () => {
  const { permissions } = usePermissions();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/redemption-settings");
      const s = res.data.data || {};
      form.setFieldsValue({
        minRedemption: s.minRedemption,
        userMonthlyCapPoints: s.userMonthlyCapPoints,
        maxRebatePercent: s.maxRebatePercent,
        pointsExpiryMonths: s.pointsExpiryMonths,
        pointsPerRm: s.pointsPerRm ?? 200,
      });
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const onFinish = async (values) => {
    setSaving(true);
    try {
      const body = {
        minRedemption: values.minRedemption,
        userMonthlyCapPoints: values.userMonthlyCapPoints,
        maxRebatePercent: values.maxRebatePercent,
        pointsExpiryMonths: values.pointsExpiryMonths,
        pointsPerRm: values.pointsPerRm,
      };
      await axios.patch("/api/redemption-settings", body);
      message.success("Settings updated");
      fetchSettings();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setSaving(false);
    }
  };

  const canEdit = permissions?.includes(PERMISSIONS.UPDATE_REDEMPTION_SETTINGS);

  return (
    <div>
      <PageHeader
        title="Redemption Settings"
        childRoutes={[{ path: "/redemption-settings", breadcrumbName: "Redemption Settings" }]}
      />

      <Spin spinning={loading}>
        <Card>
          <Form form={form} layout="vertical" onFinish={onFinish} disabled={!canEdit}>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="minRedemption"
                  label="Minimum redemption (points)"
                  rules={[
                    {
                      required: true,
                      type: "number",
                      min: 1,
                      message: "Must be greater than 0",
                    },
                  ]}
                  tooltip="Floor for both direct points-at-checkout and voucher redemption."
                >
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="userMonthlyCapPoints"
                  label="Per-user monthly cap (points)"
                  rules={[
                    {
                      required: true,
                      type: "number",
                      min: 1,
                      message: "Must be greater than 0",
                    },
                  ]}
                  tooltip="Maximum points a single user can redeem per Asia/KL calendar month. Default 100,000 = RM500 at 200pt=RM1."
                >
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="maxRebatePercent"
                  label="Max points discount per order (%)"
                  rules={[
                    {
                      required: true,
                      type: "number",
                      min: 0.01,
                      max: 100,
                      message: "Must be greater than 0 and at most 100",
                    },
                  ]}
                  tooltip="Caps the share of an order's value payable with points at checkout (points-at-checkout only, NOT reward vouchers). This is a percentage; it is unrelated to a reward voucher's fixed RM face value, which is set per reward item. 5% default."
                >
                  <InputNumber min={0.01} max={100} step={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="pointsExpiryMonths"
                  label="Points expiry (months from earn date)"
                  rules={[
                    {
                      required: true,
                      type: "number",
                      min: 1,
                      message: "Must be greater than 0",
                    },
                  ]}
                  tooltip="Set on every new EARN transaction. 12 default."
                >
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="pointsPerRm"
                  label="Points per RM (redeem rate)"
                  rules={[
                    {
                      required: true,
                      type: "number",
                      min: 1,
                      message: "Must be greater than 0",
                    },
                  ]}
                  tooltip="Points needed per RM1 of checkout discount. 200 default (200 pts = RM1)."
                >
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            {canEdit && (
              <Button type="primary" htmlType="submit" loading={saving}>
                Save settings
              </Button>
            )}
          </Form>
        </Card>
      </Spin>
    </div>
  );
};

export default RedemptionSettings;
